'use client';
/**
 * Arrivée d'une personne — le geste qui manquait (lot H4).
 *
 * Jusqu'ici créer quelqu'un passait par un script SQL : les quatre comptes
 * absents de l'organigramme ont dû être insérés à la main. Le RQ doit pouvoir
 * le faire ici — décision du 29/07/2026.
 *
 * Ce que l'écran refuse de faire tout seul :
 *  · attribuer des habilitations. Une personne qui arrive n'en a aucune, et
 *    c'est le sens de la décision du RQ : seuls les anciens ont été repris par
 *    ancienneté, les nouveaux suivent formation → habilitation → prise de poste.
 *    Créer quelqu'un « déjà habilité » fabriquerait la preuve qu'on cherche
 *    justement à produire ;
 *  · l'inscrire dans l'organigramme. Y faire figurer quelqu'un est une décision
 *    d'affichage, distincte de son embauche — d'où une case, décochée.
 *
 * ── Le compte de connexion (lot GED-7) ──────────────────────────────────────
 *
 * L'écran ne mentionnait NI compte NI identifiants, et le paramétrage renvoyait
 * au module Site. Or une personne créée sans compte est une personne qui ne
 * recevra aucune diffusion et n'accusera aucune lecture — c'est ainsi qu'un lot
 * précédent a découvert qu'une audience de diffusion ne désignait personne.
 *
 * Le compte est donc PROPOSÉ ici, jamais créé d'office : dans ce laboratoire,
 * treize personnes sur vingt-trois n'en ont aucun, et c'est l'organisation
 * réelle, pas un oubli. Le profil suggéré se DÉDUIT de la fonction — la règle
 * vit côté serveur (`backend/src/rh-comptes.ts`), elle est testée, et cet écran
 * la lit plutôt que de la recopier : deux tables divergent, et celle de l'écran
 * finirait par proposer un profil que le serveur refuse.
 *
 * ⚠️ AUCUN MOT DE PASSE n'est engendré, affiché, pré-rempli ni saisi ici, et il
 * n'y a pas de champ pour en mettre un. Le compte naît non confirmé, avec un
 * jeton d'invitation de 48 h ; la personne choisit son mot de passe elle-même.
 *
 * ── Les trois demandes de la RQ du 01/09/2026 ───────────────────────────────
 *
 * 1. LA FONCTION SE CHOISIT DANS UNE LISTE. Elle était en texte libre, et c'est
 *    ce champ qui commande le profil de connexion proposé : une faute de frappe
 *    ne produisait pas d'erreur, elle produisait « fonction non reconnue, aucun
 *    profil n'est présumé ». La liste est celle de l'organigramme fonctionnel
 *    (`ged-setting.orgFonctions`) — la MÊME source que les cases cochées dans
 *    l'éditeur d'organigramme, et la même que celle où pointent les grilles de
 *    compétences (`f_technicien`, cf. `backend/src/fonctions-referentiel.ts`).
 *
 *    ⚠️ Ce n'est donc plus seulement un libellé qui part au serveur : la
 *    fonction choisie pose AUSSI `fonctionIds` et `orgTier`. La personne est
 *    rattachée à sa case dès son arrivée, là où il fallait auparavant rouvrir
 *    « Modifier l'organigramme » pour cocher « Postes tenus » — une seconde
 *    étape que rien ne réclamait et que personne ne faisait.
 *
 *    L'intitulé AFFICHÉ reste ce que porte `rh-personnel.fonction`, et il peut
 *    être précisé ensuite dans l'organigramme (« · pilote Analytique ») : c'est
 *    la ligne posée en tête de `fonctions-referentiel.ts`, on ne la change pas.
 *
 * 2. PRÉNOM ET NOM SÉPARÉS. `rh-personnel.prenom` existait dans le modèle et
 *    n'était rempli pour personne (0 sur 30) : le serveur le devinait en
 *    coupant le nom complet au premier espace. C'est faux pour « Wafa Hadj
 *    Hassine », et c'est ce prénom deviné que le mémo du jour affiche en tête
 *    du tableau de bord (« عسلامة … »). Les deux champs sont désormais saisis,
 *    et `nom` reste le nom complet affiché — le changer aurait défait
 *    l'affichage de trente fiches pour un gain nul.
 *
 * 3. ON NE SORT PLUS DE LA FENÊTRE PAR ACCIDENT. Le voile se refermait sur un
 *    simple clic à côté de la boîte. La protection est celle du socle
 *    (`Modale protegee`) : voile, Échap et croix passent par une question,
 *    et le navigateur en pose une seconde si l'on recharge ou ferme l'onglet.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import type { PropositionCompte } from '@/lib/types';
import { I, Modale, inputStyle } from '@/components/ui';
import { lbl } from '@/components/screens/RhAbsences';
import { nomDe } from '@/lib/nom';

import { Bouton } from '@/shared/Bouton';
import { aujourdhuiIso } from '@/lib/meta';
/** `tech.dupont` — minuscules, chiffres et points : la clé de tout le module. */
const RE_ID = /^[a-z0-9.]+$/;

/**
 * Le préfixe d'identifiant que le laboratoire emploie pour chaque fonction.
 *
 * Relevé sur l'effectif réel, et non inventé : `tech.ftiti`, `prel.majbri`,
 * `resp.hmidet`, `compta.marzouki`, `log.belhadjamor`, `ent.sayeh`. Le code
 * suggérait `tech.` À TOUT LE MONDE — invisible tant que la fonction était en
 * texte libre, franchement faux dès qu'on peut choisir « Préleveur ».
 *
 * ⚠️ Ce n'est qu'une SUGGESTION, et elle s'efface dès qu'on écrit dans le
 * champ : l'identifiant ne se corrige plus après coup, il n'est pas question
 * qu'une table le décide à la place de la personne qui saisit. Les deux
 * fonctions que le laboratoire n'a jamais eu à nommer (`prep`, `secr`) suivent
 * la même construction que les six autres.
 */
const PREFIXE_ORGID: Record<string, string> = {
  f_biologiste: 'resp',
  f_technicien: 'tech',
  f_preparateur: 'prep',
  f_preleveur: 'prel',
  f_secretaire: 'secr',
  f_comptable: 'compta',
  f_coursier: 'log',
  f_entretien: 'ent',
};

/** Minuscules sans accents ni ponctuation — la forme d'un identifiant. */
const cleDe = (s: string) => s.trim().toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]/g, '');

type Champs = Record<string, string | boolean>;

export function FormulaireArrivee({ onFermer, onCree }: {
  onFermer: () => void;
  onCree?: (orgId: string) => void;
}) {
  const { creerPersonnel, rhPersonnel, profilPourFonction, settings } = useGed();

  /* L'état de DÉPART est gardé tel quel : c'est lui, et non une liste de champs
     recopiée à la main, qui dit si quelque chose a été saisi. Une liste se
     désynchronise du formulaire au premier champ ajouté, et la fenêtre
     redeviendrait alors silencieusement perdable. */
  const [depart] = React.useState<Champs>(() => ({
    orgId: '', prenom: '', nom: '', fonctionId: '', badgeId: '',
    dateEntree: aujourdhuiIso(),
    /* ⚠️ COCHÉE, et c'est un renversement assumé du 01/09/2026.
     *
     * Elle naissait décochée, au motif — juste — que figurer à l'organigramme
     * est une décision d'affichage distincte de l'embauche. Mais `gedUsers` est
     * la SEULE liste que lisent la fiche de la personne et l'arbre : décochée,
     * la personne était créée puis introuvable. Sa fiche ne s'ouvrait même pas
     * (`if (!u) return null`), et le glisser-déposer ne pouvait pas l'atteindre
     * pour lui donner sa place — les deux signalements de la RQ.
     *
     * Le motif d'origine est désormais couvert autrement : le rattachement se
     * choisit ci-dessous, donc il n'est plus « pas encore arrêté » au moment de
     * la création. La case reste, pour le prestataire ou l'erreur de saisie. */
    site: '', responsableOrgId: '', lienResponsable: 'hierarchique', dansOrganigramme: true,
    email: '',
  }));
  const [f, setF] = React.useState<Champs>(depart);
  const [envoi, setEnvoi] = React.useState(false);
  const maj = (k: string, v: string | boolean) => setF((x) => ({ ...x, [k]: v }));

  /* ---- Profil de connexion : proposé, modifiable, refusable ---------------
   * `null` dans `profil` n'est PAS l'absence de réponse : c'est « aucun compte »,
   * et c'est un choix de plein droit. On distingue donc les deux avec
   * `choixFait` — sans quoi la proposition du serveur écraserait, à chaque
   * changement de fonction, un refus que le RQ vient d'exprimer. */
  const [proposition, setProposition] = React.useState<PropositionCompte | null>(null);
  const [profil, setProfil] = React.useState<string>('');
  const [choixFait, setChoixFait] = React.useState(false);

  const demanderProposition = React.useCallback(async (fonction: string) => {
    const p = await profilPourFonction(fonction);
    if (!p) return;
    setProposition(p);
    // La proposition ne s'impose qu'à qui n'a pas encore tranché.
    setProfil((actuel) => (choixFait ? actuel : (p.profil || '')));
  }, [profilPourFonction, choixFait]);

  /* ---- La fonction, choisie dans l'organigramme fonctionnel --------------- */
  const fonctions = settings?.orgFonctions || [];
  const fonctionId = String(f.fonctionId || '');
  const fonction = fonctions.find((x) => x.id === fonctionId) || null;

  const choisirFonction = (id: string) => {
    maj('fonctionId', id);
    const label = fonctions.find((x) => x.id === id)?.label || '';
    if (label) void demanderProposition(label);
    else setProposition(null);
  };

  /** Qui peut porter le trait : l'effectif en service, les externes exclus —
   *  un confrère n'encadre personne. Trié par nom, comme la liste se lit. */
  const encadrants = React.useMemo(
    () => rhPersonnel
      .filter((p) => p.statut !== 'sorti' && p.type !== 'externe')
      .slice()
      .sort((a, b) => a.nom.localeCompare(b.nom)),
    [rhPersonnel],
  );

  /* ---- Le site (01/09/2026) ----------------------------------------------
   * Il n'y avait AUCUN champ : l'arrivée envoyait `site: ''`, et la personne
   * naissait sans site quand les vingt-trois autres portent « Monastir —
   * Ruspina ». Invisible tant qu'il n'y a qu'un site, faux le jour où il y en
   * a deux — et alors faux rétroactivement, sur les fiches déjà créées.
   *
   * ⚠️ UNE LISTE MÊME À UNE SEULE ENTRÉE, c'est la demande de la RQ et c'est le
   * bon choix : un champ pré-rempli qu'on ne peut pas changer se lit comme une
   * valeur imposée par le logiciel, une liste d'un élément se lit comme un
   * choix que le laboratoire a fait. Le jour du second site, il n'y a rien à
   * reprendre.
   *
   * La valeur par défaut est DÉRIVÉE et non recopiée dans l'état : `settings`
   * peut arriver après le premier rendu, et un état initialisé trop tôt aurait
   * figé la chaîne vide. */
  const sites = settings?.sites || [];
  const site = String(f.site || '') || settings?.activeSite || '';

  const prenom = String(f.prenom || '');
  const nom = String(f.nom || '');
  /** ⚠️ CORRIGÉ LE 07/09/2026 — CET ÉCRAN RECRÉAIT LE MÉLANGE.
   *  Il composait « Naim Ftiti » et l'écrivait dans la colonne `nom`, au motif
   *  que les trente fiches existantes le portaient ainsi. Or la colonne a été
   *  séparée le 06/09 : `nom` est le PATRONYME SEUL, et `prenom` le porte à
   *  part. Chaque arrivée saisie depuis remettait donc le prénom dans `nom` —
   *  silencieusement, et pour toujours, puisque rien ne relit une fiche créée.
   *  L'affichage se compose à la lecture (`nomDe`), il ne se stocke plus. */

  /* ---- L'identifiant : suggéré tant qu'on n'y a pas touché ---------------- */
  const [orgIdTouche, setOrgIdTouche] = React.useState(false);
  const suggestion = React.useMemo(() => {
    const base = cleDe(nom);
    const prefixe = PREFIXE_ORGID[fonctionId];
    // Sans fonction choisie, aucun préfixe ne se devine : on ne suggère RIEN
    // plutôt qu'un `tech.` qui serait faux cinq fois sur huit.
    return base && prefixe ? `${prefixe}.${base}` : '';
  }, [nom, fonctionId]);
  /* La suggestion est CALCULÉE, jamais recopiée dans l'état par un effet : le
     nom et la fonction changent tous deux, et deux effets qui se répondent sur
     le même champ finissent par écraser ce que l'on vient de taper. Dès qu'on
     écrit dans le champ, `orgIdTouche` fige la valeur saisie et la suggestion
     ne revient plus. */
  const orgId = orgIdTouche ? String(f.orgId || '') : suggestion;
  // Contre le PERSONNEL, pas contre l'organigramme : quelqu'un créé hors
  // organigramme n'y figure pas, et l'écran aurait annoncé libre un identifiant
  // que le serveur refuse — un message faux est pire que pas de message.
  const dejaPris = rhPersonnel.some((p) => p.orgId === orgId);
  const idInvalide = !!orgId && !RE_ID.test(orgId);
  const email = String(f.email || '').trim();
  // Un profil choisi SANS adresse ne peut aboutir à rien : l'invitation ne part
  // pas. On bloque à la saisie plutôt que de laisser le serveur créer la
  // personne puis annoncer que le compte a échoué.
  const mailInvalide = !!profil && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const pretA = !!orgId && !!prenom.trim() && !!nom.trim()
    && !idInvalide && !dejaPris && !mailInvalide;

  /* ---- Sortie protégée ---------------------------------------------------
   * « Saisi » se lit contre l'état de départ, `choixFait` compris : refuser un
   * compte est une décision, et la reperdre en refermant la fenêtre ferait
   * recommencer le raisonnement, pas seulement la frappe. */
  const saisi = React.useMemo(
    () => choixFait || Object.keys(depart).some((k) => f[k] !== depart[k]),
    [f, depart, choixFait],
  );
  const [sortieDemandee, setSortieDemandee] = React.useState(false);
  /** La question ne se pose que tant qu'il y a quelque chose à perdre : vider
   *  le dernier champ la retire, sans qu'un effet ait à défaire un état. */
  const confirmerSortie = sortieDemandee && saisi;

  /* Le navigateur, lui, ne passe pas par notre voile : recharger la page ou
     fermer l'onglet emporte la saisie sans que React soit prévenu. La fenêtre
     de confirmation est celle du navigateur, son texte n'est pas le nôtre —
     mais la question est posée, et c'est tout ce qu'on peut en obtenir. */
  React.useEffect(() => {
    if (!saisi || envoi) return undefined;
    const avant = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', avant);
    return () => window.removeEventListener('beforeunload', avant);
  }, [saisi, envoi]);

  const quitter = () => { if (saisi) setSortieDemandee(true); else onFermer(); };

  /* La fenêtre est protégée PENDANT la création aussi. Le voile la refermait au
     clic, création en cours comprise — et l'on ne voyait alors jamais le
     message qui dit si l'invitation est partie. Tant que le serveur n'a pas
     répondu, sortir ne fait rien ; la fermeture légitime vient de `creer`. */
  const protegee = saisi || envoi;

  const creer = async () => {
    setEnvoi(true);
    try {
      const rep = await creerPersonnel({
        orgId, nom: nom.trim(), prenom: prenom.trim(),
        // Le LIBELLÉ pour l'affichage, l'IDENTIFIANT pour le rattachement : les
        // deux, parce qu'ils ne servent pas à la même chose (cf. l'en-tête).
        /* `fonction` est encore ENVOYÉ, mais plus STOCKÉ (02/09/2026) : le
           serveur s'en sert pour déduire le profil de compte à proposer, et
           l'intitulé, lui, est calculé depuis `fonctionIds`. Le retirer ferait
           retomber la proposition sur « fonction non reconnue ». */
        fonction: fonction?.label || '',
        fonctionIds: fonctionId ? [fonctionId] : [],
        orgTier: fonction?.tier || '', site,
        responsableOrgId: String(f.responsableOrgId || ''),
        lienResponsable: String(f.lienResponsable || 'hierarchique'),
        badgeId: f.badgeId || '',
        dateEntree: f.dateEntree || '',
        dansOrganigramme: f.dansOrganigramme === true,
        // Profil vide = refus explicite : la clé n'est pas envoyée, et
        // le serveur n'a donc rien à créer ni à refuser.
        ...(profil ? { compte: { profil, email } } : {}),
      });
      /* On ne referme PAS quand l'invitation a échoué : la personne est créée,
         mais le RQ doit lire pourquoi le compte ne l'est pas — fermer la
         fenêtre emporterait le seul endroit où c'est dit.
         ⚠️ NI QUAND LE COURRIEL N'EST PAS PARTI (13/09/2026). Le compte existe
         alors, mais la personne l'ignore : c'est le cas où l'on croit le geste
         terminé alors qu'il reste à relancer l'envoi. Refermer sur un bandeau
         qui disparaît serait le meilleur moyen de ne jamais le relancer. */
      if (rep?.compte?.erreur) return;
      if (rep?.compte?.cree && rep.compte.courrielEnvoye === false) return;
      if (rep?.ok) { onCree?.(orgId); onFermer(); }
    } finally { setEnvoi(false); }
  };

  const pied = confirmerSortie ? (
    <>
      <span style={{ flex: 1, fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.5, textAlign: 'start' }}>
        Abandonner cette arrivée ? Ce qui est saisi sera perdu.
      </span>
      <Bouton taille="sm" onClick={onFermer}>Abandonner</Bouton>
      <Bouton variante="primaire" taille="sm" autoFocus onClick={() => setSortieDemandee(false)}>Continuer la saisie</Bouton>
    </>
  ) : (
    <>
      <Bouton taille="sm" onClick={quitter} disabled={envoi}>Annuler</Bouton>
      <Bouton variante="primaire" disabled={!pretA || envoi} onClick={() => void creer()}>
        <I n="check" s={14} /> {envoi ? 'Création…' : 'Créer'}
      </Bouton>
    </>
  );

  return (
    <Modale titre="Arrivée d’une personne" onClose={onFermer} largeur={620}
      protegee={protegee} pied={pied}
      onSortieBloquee={() => { if (!envoi) setSortieDemandee(true); }}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={lbl}>Prénom
            <input style={{ ...inputStyle, width: '100%' }} value={prenom} autoFocus
              onChange={(e) => maj('prenom', e.target.value)} placeholder="Naim" />
          </label>
          <label style={lbl}>Nom
            <input style={{ ...inputStyle, width: '100%' }} value={nom}
              onChange={(e) => maj('nom', e.target.value)} placeholder="Ftiti" />
          </label>
        </div>

        <label style={lbl}>Fonction
          {/* ⚠️ `settings` NUL n'est pas « aucune fonction » : c'est « on ne sait
              pas encore ». Une liste vide servie pendant le chargement se lit
              comme une affirmation — l'organigramme fonctionnel ne déclarerait
              rien — et l'on refermerait la fenêtre en croyant le paramétrage à
              refaire (relevé BLOQ-03). */}
          <select style={{ ...inputStyle, width: '100%' }} value={fonctionId}
            disabled={!settings} onChange={(e) => choisirFonction(e.target.value)}>
            <option value="">{settings ? '— choisir une fonction —' : 'Chargement…'}</option>
            {fonctions.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
          <span style={{ display: 'block', fontSize: 12.5, fontWeight: 400, marginTop: 4, lineHeight: 1.5,
            color: settings && !fonctions.length ? 'var(--amber-600)' : 'var(--text-muted)' }}>
            {settings && !fonctions.length
              ? 'L’organigramme fonctionnel ne déclare aucune fonction : la personne sera créée sans rattachement.'
              : 'Elle rattache la personne à sa case de l’organigramme et décide du profil de '
                + 'connexion proposé ci-dessous. L’intitulé affiché sur l’organigramme en '
                + 'découle : il est calculé, et se complète en désignant la personne pilote '
                + 'd’un processus sur la cartographie.'}
          </span>
        </label>

        <label style={lbl}>Site
          <select style={{ ...inputStyle, width: '100%' }} value={site}
            disabled={!settings} onChange={(e) => maj('site', e.target.value)}>
            {/* Pas d'entrée vide quand un site est déclaré : « aucun site » n'est
                pas un choix que ce laboratoire propose, et l'offrir ferait naître
                des fiches sans site — ce que le champ absent faisait déjà. */}
            {!settings && <option value="">Chargement…</option>}
            {settings && !sites.length && <option value="">Aucun site déclaré</option>}
            {sites.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          {settings && !sites.length && (
            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 400, marginTop: 4, lineHeight: 1.5, color: 'var(--amber-600)' }}>
              Aucun site n’est déclaré au paramétrage : la personne sera créée sans site.
            </span>
          )}
        </label>

        {/* « CLÉ INTERNE » ET NON « IDENTIFIANT » (RQ, 03/10/2026) : ce champ est
            l'`orgId`, scellé au journal et jamais renommé. L'appeler « identifiant »
            le faisait prendre pour ce qu'on tape à la connexion — qui, lui, se
            modifie ensuite depuis le dossier (« Identifiant de connexion »). */}
        <label style={lbl}>Clé interne
          <input style={{ ...inputStyle, width: '100%' }} value={orgId}
            onChange={(e) => { setOrgIdTouche(true); maj('orgId', e.target.value.toLowerCase()); }}
            placeholder={suggestion || 'tech.dupont'} />
          <span style={{ display: 'block', fontSize: 12.5, fontWeight: 400, marginTop: 4, lineHeight: 1.5,
            color: idInvalide || dejaPris ? 'var(--amber-600)' : 'var(--text-muted)' }}>
            {dejaPris
              ? `« ${orgId} » est déjà pris par quelqu’un.`
              : idInvalide
                ? 'Minuscules, chiffres et points uniquement.'
                : 'Cette clé relie la personne à son planning, ses congés et ses habilitations ; elle ne se change jamais. Si un compte est créé, elle sert d’identifiant de connexion de départ — celui-ci se modifie ensuite depuis le dossier.'}
          </span>
        </label>

        {/* ---- Sa place dans la hiérarchie (01/09/2026) ------------------- */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <label style={lbl}>Rattaché(e) à
            <select style={{ ...inputStyle, width: '100%' }} value={String(f.responsableOrgId || '')}
              onChange={(e) => maj('responsableOrgId', e.target.value)}>
              <option value="">Racine de l’organigramme</option>
              {encadrants.map((p) => (
                <option key={p.orgId} value={p.orgId}>{nomDe(p)}{p.fonction ? ` — ${p.fonction}` : ''}</option>
              ))}
            </select>
          </label>
          <label style={lbl}>Lien
            <select style={{ ...inputStyle, width: '100%' }} value={String(f.lienResponsable || 'hierarchique')}
              disabled={!f.responsableOrgId}
              onChange={(e) => maj('lienResponsable', e.target.value)}>
              <option value="hierarchique">Hiérarchique</option>
              <option value="fonctionnel">Fonctionnel</option>
            </select>
          </label>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '-4px 0 0', lineHeight: 1.5 }}>
          Le trait qui la relie à son responsable sur l’organigramme. Laissé vide, elle se place à
          la racine, à côté de la direction — ce qui est rarement ce qu’on veut. Le lien
          <strong> fonctionnel</strong> se trace en pointillés : il dit une autorité sans lien
          hiérarchique, comme l’indépendance de la RQ. Il se corrige ensuite au glisser-déposer.
        </p>

        {/* ---- Compte de connexion (GED-7) ------------------------------- */}
        <fieldset style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
          padding: '12px 14px 14px', margin: 0 }}>
          <legend style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', padding: '0 6px' }}>
            Compte de connexion
          </legend>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* « Aucun compte » EN PREMIER et sur le même pied que les autres :
                c'est le cas majoritaire ici, et l'enterrer en dernier le ferait
                lire comme un renoncement plutôt que comme un choix. */}
            {[{ cle: '', label: 'Aucun compte', aide: 'La personne existe, est planifiable et payée, mais ne se connecte pas.' },
              ...(proposition?.profils || [])].map((p) => {
              const on = profil === p.cle;
              const suggere = (proposition?.profil || '') === p.cle && !!proposition;
              return (
                <button key={p.cle || 'aucun'} type="button" role="radio" aria-checked={on} title={p.aide}
                  onClick={() => { setProfil(p.cle); setChoixFait(true); }}
                  style={{
                    cursor: 'pointer', fontSize: 12.5, padding: '6px 12px', textAlign: 'start',
                    borderRadius: 'var(--radius-pill)', fontWeight: on ? 700 : 400,
                    border: `1px solid ${on ? 'var(--color-primary)' : 'var(--border-default)'}`,
                    background: on ? 'var(--color-primary)' : 'transparent',
                    color: on ? 'var(--surface-card)' : 'var(--text-body)',
                  }}>
                  {p.label}
                  {suggere && <span style={{ marginInlineStart: 6, fontSize: 12.5, opacity: 0.85 }}>· proposé</span>}
                </button>
              );
            })}
          </div>

          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '9px 0 0', lineHeight: 1.55 }}>
            {proposition
              ? proposition.motif
              : 'Choisissez la fonction : un profil sera proposé. Vous pouvez le changer ou le refuser.'}
          </p>

          {profil ? (
            <>
              <label style={{ ...lbl, marginTop: 12, display: 'block' }}>Adresse de courriel
                <input type="email" style={{ ...inputStyle, width: '100%' }} value={email}
                  onChange={(e) => maj('email', e.target.value)}
                  placeholder={orgId ? `${orgId}@bio24.tn` : 'prenom.nom@bio24.tn'} />
                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 400, marginTop: 4, lineHeight: 1.5,
                  color: mailInvalide ? 'var(--amber-600)' : 'var(--text-muted)' }}>
                  {mailInvalide
                    ? 'Adresse invalide : sans elle, l’invitation ne peut pas partir.'
                    : 'L’invitation y sera envoyée. L’identifiant de connexion du compte sera, au départ, « ' + (orgId || '…') + ' » (la clé interne) ; il se modifie ensuite depuis le dossier.'}
                </span>
              </label>
              {/* ⚠️ LE COURRIEL PART-IL VRAIMENT ? Tous les envois de la
                  plateforme sont en « fire-and-forget » : l'échec s'écrit dans
                  le journal du serveur et l'écran continue. Tant que le relais
                  n'est pas renseigné, l'invitation n'arrive nulle part et
                  l'écran répond « envoyée ». On attend, on relance, puis on met
                  en cause la personne. Le dire AVANT le geste, pas après. */}
              <BandeauCourriel />
              {/* Dire explicitement qu'aucun mot de passe ne se saisit ici :
                  c'est le champ que tout le monde cherche, et son absence doit
                  se lire comme une décision, pas comme un oubli. */}
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '8px 0 0', lineHeight: 1.55 }}>
                <strong>Aucun mot de passe ne se saisit ici.</strong> La personne recevra un lien valable
                48 heures et choisira le sien. Tant qu’elle ne l’a pas fait, le compte existe mais ne
                permet aucune connexion.
              </p>
            </>
          ) : (
            <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '8px 0 0', lineHeight: 1.55 }}>
              Sans compte, cette personne <strong>ne recevra aucune diffusion et n’accusera aucune lecture</strong>.
              Elle reste planifiable, habilitable et payée. Un compte pourra lui être ouvert plus tard.
            </p>
          )}
        </fieldset>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {/* Un seul numéro de badgeuse (03/10/2026) : l'ancien « Matricule »
              en était le double. Le matricule de PAIE se saisit au dossier. */}
          <label style={lbl}>N° de badge
            <input style={{ ...inputStyle, width: '100%' }} value={String(f.badgeId || '')}
              onChange={(e) => maj('badgeId', e.target.value)} />
          </label>
          <label style={lbl}>Date d’entrée
            <input type="date" style={{ ...inputStyle, width: '100%' }} value={String(f.dateEntree || '')}
              onChange={(e) => maj('dateEntree', e.target.value)} />
          </label>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '-4px 0 0', lineHeight: 1.5 }}>
          Sans badge, les pointages de cette personne resteront orphelins : la badgeuse
          n’émet qu’un numéro, c’est lui qui fait le lien.
        </p>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer', marginTop: 4 }}>
          <input type="checkbox" style={{ marginTop: 3, flex: 'none' }}
            checked={f.dansOrganigramme === true}
            onChange={(e) => maj('dansOrganigramme', e.target.checked)} />
          <span style={{ fontSize: 12.5, color: 'var(--text-strong)', lineHeight: 1.5 }}>
            Faire figurer dans l’organigramme
            {/* Ce que DÉCOCHER coûte, dit avant le geste et non après : c'est
                par là que la personne devenait introuvable. */}
            <span style={{ display: 'block', fontSize: 12.5,
              color: f.dansOrganigramme === true ? 'var(--text-muted)' : 'var(--amber-600)', fontWeight: 400 }}>
              {f.dansOrganigramme === true
                ? 'Elle apparaîtra sur le document d’organisation, à la place choisie ci-dessus.'
                : 'Décochée, la personne existe et reste planifiable, payée et habilitable — mais elle '
                  + 'n’apparaît NI sur l’organigramme NI dans l’arbre : sa fiche ne s’ouvre pas depuis '
                  + 'cet écran, et son rattachement ne peut plus être corrigé au glisser-déposer. À '
                  + 'réserver au prestataire, ou à une saisie qu’on ne veut pas publier.'}
            </span>
          </span>
        </label>

        <div style={{ background: 'var(--gray-25)', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)', padding: '14px 16px', marginTop: 4 }}>
          <strong style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>Ensuite, dans cet ordre</strong>
          <ol style={{ margin: '6px 0 0', paddingInlineStart: 18, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.7 }}>
            <li>Son <strong>contrat</strong> — c’est lui qui porte le droit à congé réel.</li>
            <li>Sa <strong>formation</strong>, puis ses habilitations au niveau « en formation ».</li>
            <li>Ses habilitations réelles, une fois l’évaluation conduite.</li>
            <li>Alors seulement, sa <strong>prise de poste</strong> au planning.</li>
          </ol>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '8px 0 0', lineHeight: 1.5 }}>
            Aucune habilitation n’est accordée ici. La reprise par ancienneté ne valait que pour
            les personnes déjà en poste — un nouvel arrivant se voit évaluer.
          </p>
        </div>
      </div>
    </Modale>
  );
}

/**
 * L'état du relais de courriel, dit là où l'on s'apprête à en envoyer un.
 *
 * ⚠️ Il lit la CONFIGURATION, pas la disponibilité du serveur : un hôte
 * renseigné mais injoignable passe ce contrôle. La phrase le dit — « configuré »
 * ne veut pas dire « ça marche », et laisser croire l'inverse recréerait le
 * défaut qu'on corrige.
 *
 * Silencieux quand tout va bien : un bandeau vert permanent au-dessus d'un
 * formulaire s'arrête d'être lu, et c'est justement le jour où il devient
 * rouge qu'on ne le verrait plus.
 */
function BandeauCourriel() {
  const [etat, setEtat] = React.useState<{ configure: boolean; manques: string[]; consequence: string } | null>(null);

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/courriel/etat', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant && j?.ok) setEtat(j); })
      .catch(() => { /* Un diagnostic indisponible ne doit pas bloquer une arrivée. */ });
    return () => { vivant = false; };
  }, []);

  if (!etat || etat.configure) return null;
  return (
    <div style={{
      marginTop: 10, padding: '10px 12px', borderRadius: 'var(--radius-sm)',
      background: 'var(--amber-50)', border: '1px solid var(--amber-500)',
    }}>
      <strong style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
        Le courriel n’est pas configuré — l’invitation ne partira pas.
      </strong>
      <p style={{ fontSize: 12.5, margin: '5px 0 0', lineHeight: 1.55 }}>{etat.consequence}</p>
      <ul style={{ margin: '5px 0 0', paddingInlineStart: 18, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-muted)' }}>
        {etat.manques.map((m) => <li key={m}>{m}</li>)}
      </ul>
      <p style={{ fontSize: 12.5, margin: '6px 0 0', lineHeight: 1.55, color: 'var(--text-muted)' }}>
        Vous pouvez tout de même créer l’arrivée : le lien d’invitation est écrit dans le journal du
        serveur et peut être transmis à la main.
      </p>
    </div>
  );
}
